import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Banknote, CheckCircle2, IndianRupee, Wallet } from 'lucide-react';
import { Button, EmptyState, Skeleton, TextInput, Modal } from '../ui';
import { formatMinutes, formatRupees } from '../utils/timeFormat';

let API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000';
if (API_URL.endsWith('/api')) {
    API_URL = API_URL.slice(0, -4);
}
if (API_URL.endsWith('/')) {
    API_URL = API_URL.slice(0, -1);
}

const fmtTime = (value) => (value ? moment(value).format('DD MMM, hh:mm A') : '-');

// Still owed on a bill (table time + GST) once table time charged up front (Add Spend) is taken off
const billDue = (bill) =>
    Math.max(0, (bill.charge || 0) + (bill.gstAmount || 0) - (bill.prepaidAmount || 0));

/**
 * Cashier settlement for a player's unpaid bills:
 *  - collect cash at the desk and mark the bills paid
 *  - debit the bills from the player's wallet
 *  - take cash into the wallet, which then settles the bills automatically
 * `apiBase` serves GET /player/:id, POST /player/:id/settle and /player/:id/deposit,
 * each answering { bills, totalDue, walletBalance }. `columns` are
 * [{ label, render(bill), align? }]; `amountOf(bill)` is what is still owed.
 * `allowCash: false` hides cash collection and the add-cash-to-wallet box.
 */
const BillSettleDialog = ({
    open, onClose, player, token, onSettled, title, noun, apiBase, columns, amountOf,
    allowCash = true,
}) => {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const [depositAmount, setDepositAmount] = useState('');
    const [notes, setNotes] = useState('');

    const playerId = player?._id;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const load = useCallback(async () => {
        if (!playerId || !token) return;
        setLoading(true);
        try {
            const { data: res } = await axios.get(`${API_URL}${apiBase}/player/${playerId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(res.data);
            const shortfall = res.data.totalDue - res.data.walletBalance;
            setDepositAmount(String(shortfall > 0 ? shortfall : res.data.totalDue));
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load unpaid bills');
        } finally {
            setLoading(false);
        }
    }, [playerId, token, apiBase]);

    useEffect(() => {
        if (open) {
            setNotes('');
            load();
        } else {
            setData(null);
        }
    }, [open, load]);

    const run = async (request, closeAfter = false) => {
        setBusy(true);
        try {
            const { data: res } = await request();
            toast.success(res.message);
            setData(res.data);
            onSettled?.();
            if (closeAfter || res.data?.bills?.length === 0) onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to settle bills');
        } finally {
            setBusy(false);
        }
    };

    const settle = (method) =>
        run(() =>
            axios.post(
                `${API_URL}${apiBase}/player/${playerId}/settle`,
                { method, notes },
                authHeaders
            ),
            method === 'unpaid'
        );

    const depositAndSettle = () =>
        run(() =>
            axios.post(
                `${API_URL}${apiBase}/player/${playerId}/deposit`,
                { amount: Number(depositAmount), notes },
                authHeaders
            )
        );

    const bills = data?.bills || [];
    const hasBills = bills.length > 0;
    const smallestBill = hasBills ? Math.min(...bills.map(amountOf)) : 0;
    const walletCanPay = hasBills && data.walletBalance >= smallestBill;
    const validDeposit = Number(depositAmount) > 0;

    return (
        <Modal
            open={open}
            onClose={busy ? undefined : onClose}
            closeOnBackdrop={!busy}
            title={title}
            description={player ? [player.name, player.identifier].filter(Boolean).join(' · ') : undefined}
            icon={IndianRupee}
            size="lg"
            footer={
                <>
                    <Button variant="secondary" disabled={busy} onClick={onClose}>
                        Close
                    </Button>
                    <Button
                        variant="secondary"
                        icon={Wallet}
                        disabled={busy || !walletCanPay}
                        title={walletCanPay ? `Debit unpaid ${noun} from wallet` : 'Insufficient wallet balance'}
                        onClick={() => settle('wallet')}
                    >
                        Debit from wallet
                    </Button>
                    {allowCash && (
                        <Button
                            variant="success"
                            icon={CheckCircle2}
                            loading={busy}
                            disabled={!hasBills}
                            onClick={() => settle('cash')}
                        >
                            Cash collected · mark paid
                        </Button>
                    )}
                </>
            }
        >
            {loading || !data ? (
                <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-3">
                        <Skeleton className="h-16 rounded-cm-lg" />
                        <Skeleton className="h-16 rounded-cm-lg" />
                    </div>
                    <Skeleton className="h-32 rounded-cm-lg" />
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-cm-lg bg-cm-danger-soft px-4 py-3">
                            <p className="text-xs font-semibold text-cm-danger m-0">Unpaid total</p>
                            <p className="text-xl font-extrabold text-cm-danger tabular-nums m-0">{formatRupees(data.totalDue)}</p>
                        </div>
                        <div className="rounded-cm-lg bg-cm-surface-2 border border-cm-border px-4 py-3">
                            <p className="text-xs font-semibold text-cm-muted m-0">Wallet balance</p>
                            <p className="text-xl font-extrabold text-cm-text tabular-nums m-0">{formatRupees(data.walletBalance)}</p>
                        </div>
                    </div>

                    {hasBills ? (
                        <div className="overflow-x-auto rounded-cm-lg border border-cm-border">
                            <table className="w-full text-[13px]">
                                <thead className="bg-cm-surface-2">
                                    <tr>
                                        {columns.map((col) => (
                                            <th
                                                key={col.label}
                                                className={`px-3 py-2 text-xs font-semibold text-cm-muted whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                                            >
                                                {col.label}
                                            </th>
                                        ))}
                                        <th className="px-3 py-2 text-xs font-semibold text-cm-muted whitespace-nowrap text-right">
                                            Amount
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bills.map((b) => (
                                        <tr key={b._id} className="border-t border-cm-border">
                                            {columns.map((col) => (
                                                <td
                                                    key={col.label}
                                                    className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''}`}
                                                >
                                                    {col.render(b)}
                                                </td>
                                            ))}
                                            <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatRupees(amountOf(b))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <EmptyState compact icon={CheckCircle2} title={`No unpaid ${noun}`} description="This player is all settled." />
                    )}

                    {hasBills && (
                        <>
                            <TextInput
                                label="Remarks (optional)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                            {allowCash && (
                                <div className="rounded-cm-lg border border-cm-border bg-cm-surface-2 p-3">
                                    <p className="text-sm font-bold text-cm-text m-0">Add cash to wallet and settle</p>
                                    <p className="text-xs text-cm-muted mt-0.5 mb-3">
                                        The cash is credited to the wallet, then unpaid {noun} are debited from it. Any
                                        extra stays in the wallet.
                                    </p>
                                    <div className="flex flex-wrap items-end gap-2">
                                        <TextInput
                                            className="w-full sm:w-48"
                                            aria-label="Cash amount"
                                            type="number"
                                            prefix="₹"
                                            min={1}
                                            value={depositAmount}
                                            onChange={(e) => setDepositAmount(e.target.value)}
                                        />
                                        <Button
                                            variant="outline"
                                            icon={Banknote}
                                            disabled={busy || !validDeposit}
                                            onClick={depositAndSettle}
                                        >
                                            Add to wallet & settle
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </Modal>
    );
};

const TABLE_BILL_COLUMNS = [
    {
        label: 'Table / seat',
        render: (b) => `${b.table ? `${b.table.tableName} (${b.table.tableNumber})` : '-'} · Seat ${b.seatNumber}`,
    },
    { label: 'Club', render: (b) => b.club?.name || '-' },
    { label: 'Left', render: (b) => <span className="whitespace-nowrap">{fmtTime(b.endedAt)}</span> },
    { label: 'Billed time', render: (b) => <span className="tabular-nums">{formatMinutes(b.billableMinutes)}</span> },
];

/** Settle a player's unpaid table bills (Player & Table Report). */
const TableBillSettleDialog = (props) => (
    <BillSettleDialog
        {...props}
        title="Settle table bills"
        noun="bills"
        apiBase="/api/table-bills"
        columns={TABLE_BILL_COLUMNS}
        amountOf={billDue}
        allowCash={false}
    />
);

const SPEND_LABELS = {
    'food-drink': 'Food & drink',
    'table-charge': 'Table charge',
    membership: 'Membership',
    'event-entry': 'Event entry',
};

const SPEND_BILL_COLUMNS = [
    { label: 'Item', render: (b) => SPEND_LABELS[b.transactionType] || b.transactionType },
    { label: 'Club', render: (b) => b.clubId?.name || b.clubName || '-' },
    { label: 'Added', render: (b) => <span className="whitespace-nowrap">{fmtTime(b.createdAt)}</span> },
    { label: 'Remarks', render: (b) => b.adminNotes || '-' },
];

/** Settle a player's unpaid spends (Transactions > Spend). */
export const SpendBillSettleDialog = (props) => (
    <BillSettleDialog
        {...props}
        title="Settle spend bills"
        noun="spends"
        apiBase="/api/spend-bills"
        columns={SPEND_BILL_COLUMNS}
        amountOf={(b) => b.amount || 0}
    />
);

export default TableBillSettleDialog;
